/**
 * Fixed Routes Rendering Hook
 * 
 * Hook to render fixed routes on the map using Atlas's line rendering system.
 */

import { useContext, useEffect, useState, useMemo, useRef } from 'react';
import { AppContext, Line, Layer, Point, Shape } from '../../../context/context';
import { useFixedRoutes } from '../../../context/FixedRoutesContext';
import {
  convertRouteToLine,
  computeRouteGeometryHash,
  getStopsHashPart,
  generateRouteColors,
} from '../../../utils/fixedRoutes/routeToLineConverter';
import { updateRouteGeometryIncremental } from '../../../utils/fixedRoutes/incrementalRouteGeometryUpdater';
import { OpenStreetMapService } from '../../../services/osm/OpenStreetMapService';
import { useEditLayer } from '../../layer_panel/functions/layerFunctions';
import type { FixedRouteStop, FixedRoute, RouteGeometry, FixedRouteLineMetadata } from '../../../types/fixedRoutes';
import { v4 as uuid4 } from 'uuid';
import { Marker, Popup } from 'react-leaflet';
import { DivIcon } from 'leaflet';
import React from 'react';
import { summarizeFixedRouteStopServices } from '../../../utils/transitStopService';
import { FixedRouteStopPopupContent } from '../components/TransitStopPopupContent';

const FIXED_ROUTES_LAYER_NAME = 'Fixed Routes';
const OPTIMISED_FIXED_ROUTES_LAYER_NAME = 'Optimised Fixed Route Network';
const fixedRouteMarkerOsmService = new OpenStreetMapService({
  baseUrl: 'https://overpass-api.de',
  rateLimit: {
    requestsPerMinute: 30,
    requestsPerHour: 500,
  },
  cache: {
    ttl: 3600000,
    maxSize: 100,
  },
  timeout: 30000,
});

/**
 * Drop fixed-route polylines whose route no longer exists in context (every layer).
 * Must run when routes are deleted even if an async geometry conversion is still in flight.
 */
function removeFixedRouteLinesNotInRouteSet(
  mapLayers: Layer[],
  validRouteIds: Set<string>,
  editLayer: (layer: Layer, layerId: number) => void
): void {
  mapLayers.forEach((mapLayer) => {
    const nextShapes = mapLayer.shapes.filter((shape) => {
      if (shape.type !== 'line') return true;
      const line = shape as Line;
      const metadata = (line as any).fixedRouteMetadata;
      if (metadata?.type === 'fixedRoute' && metadata.fixedRouteId) {
        return validRouteIds.has(metadata.fixedRouteId);
      }
      return true;
    });
    if (nextShapes.length !== mapLayer.shapes.length) {
      editLayer({ ...mapLayer, shapes: nextShapes }, mapLayer.layerId);
    }
  });
}

/**
 * Hook to sync fixed routes to map layers
 * Automatically creates/updates a Fixed Routes layer and converts routes to Lines
 */
export function useFixedRoutesSync() {
  const context = useContext(AppContext);
  const { routes, stops, setRouteGenerationStatus, persistRouteGeometry } = useFixedRoutes();
  const editLayer = useEditLayer();

  if (!context) {
    throw new Error('useFixedRoutesSync must be used within AppContext');
  }

  const { global } = context;
  const { mapLayers, idCounter, setIdCounter } = global;

  const routesRef = useRef(routes);
  routesRef.current = routes;
  const layerNameForCurrentSync = useMemo(() => {
    const hasImported = routes.some((r) => r.simulationImport?.source === 'simulation_results');
    const hasNonImported = routes.some((r) => r.simulationImport?.source !== 'simulation_results');
    return hasImported && !hasNonImported
      ? OPTIMISED_FIXED_ROUTES_LAYER_NAME
      : FIXED_ROUTES_LAYER_NAME;
  }, [routes]);

  // Track if conversion is in progress to prevent concurrent runs
  const isConvertingRef = useRef(false);
  // When a sync is requested while one is already running we drop it (to avoid flooding the routing
  // backend), but we MUST run once more afterwards so the LATEST state is rendered. Without this, the
  // final state can be silently lost — e.g. a click's conversion is still in flight when snap moves
  // the stop, the snap's sync is dropped, and that stop's routed line then never appears.
  const pendingResyncRef = useRef(false);
  const [resyncNonce, setResyncNonce] = useState(0);
  const prevResyncNonceRef = useRef(0);

  // Find or create Fixed Routes layer
  // IMPORTANT: Only one Fixed Routes layer should exist at a time
  const fixedRoutesLayer = useMemo(() => {
    return mapLayers.find((layer) => layer.layerName === layerNameForCurrentSync);
  }, [mapLayers, layerNameForCurrentSync]);

  // CRITICAL: Create Fixed Routes layer IMMEDIATELY when routes exist AND move routes from other layers
  // This must run before the main sync effect to ensure the layer exists and routes are moved
  useEffect(() => {
    if (routes.length === 0) return;
    
    const currentLayers = global.mapLayers;
    let fixedRoutesLayer = currentLayers.find((l) => l.layerName === layerNameForCurrentSync);
    
    // If routes exist but no Fixed Routes layer, create it NOW
    if (!fixedRoutesLayer) {
      const newLayerId = idCounter;
      setIdCounter(idCounter + 1);
      const newLayer: Layer = {
        layerId: newLayerId,
        layerName: layerNameForCurrentSync,
        hidden: false,
        shapes: [],
        expanded: true,
      };
      global.setLayers((prevLayers) => {
        const otherLayers = prevLayers.filter((l) => l.layerName !== layerNameForCurrentSync);
        return [...otherLayers, newLayer];
      });
      fixedRoutesLayer = newLayer;
    }
    
    // AGGRESSIVE: Find and move ALL fixed route lines from other layers to Fixed Routes layer
    const fixedRoutesLayerId = fixedRoutesLayer.layerId;
    const routesToMove: Line[] = [];
    const layersToClean: Array<{ layer: Layer; cleanedShapes: Shape[] }> = [];
    
    currentLayers.forEach((layer) => {
      if (layer.layerId === fixedRoutesLayerId) return; // Skip Fixed Routes layer
      
      const fixedRouteLines = layer.shapes.filter((shape): shape is Line => {
        if (shape.type === 'line') {
          const line = shape as Line;
          const metadata = (line as any).fixedRouteMetadata;
          return metadata && metadata.type === 'fixedRoute';
        }
        return false;
      });
      
      if (fixedRouteLines.length > 0) {
        // Collect routes to move
        routesToMove.push(...fixedRouteLines);
        
        // Prepare cleaned layer
        const cleanedShapes = layer.shapes.filter((shape) => {
          if (shape.type === 'line') {
            const line = shape as Line;
            const metadata = (line as any).fixedRouteMetadata;
            if (metadata && metadata.type === 'fixedRoute') {
              return false; // Remove from this layer
            }
          }
          return true;
        });
        
        layersToClean.push({ layer, cleanedShapes });
      }
    });
    
    // Remove routes from other layers
    layersToClean.forEach(({ layer, cleanedShapes }) => {
      const cleanedLayer = {
        ...layer,
        shapes: cleanedShapes,
      };
      editLayer(cleanedLayer, layer.layerId);
    });
    
    // Add routes to Fixed Routes layer (if any were found)
    if (routesToMove.length > 0) {
      const currentFixedRoutesLayer = global.mapLayers.find((l) => l.layerId === fixedRoutesLayerId) || fixedRoutesLayer;
      const existingRouteIds = new Set(
        currentFixedRoutesLayer.shapes
          .filter((s): s is Line => s.type === 'line')
          .map((line) => {
            const metadata = (line as any).fixedRouteMetadata;
            return metadata?.fixedRouteId;
          })
          .filter(Boolean)
      );
      
      // Only add routes that aren't already in Fixed Routes layer
      const newRoutes = routesToMove.filter((route) => {
        const metadata = (route as any).fixedRouteMetadata;
        const routeId = metadata?.fixedRouteId;
        return routeId && !existingRouteIds.has(routeId);
      });
      
      if (newRoutes.length > 0) {
        // Update layerId to Fixed Routes layer
        const updatedRoutes = newRoutes.map((route) => ({
          ...route,
          layerId: fixedRoutesLayerId,
        }));
        
        const updatedLayer = {
          ...currentFixedRoutesLayer,
          shapes: [...currentFixedRoutesLayer.shapes, ...updatedRoutes],
        };
        editLayer(updatedLayer, fixedRoutesLayerId);
      }
    }
  }, [routes.length, global.mapLayers, idCounter, setIdCounter, global.setLayers, editLayer]);


  // Create serialized keys for comparison to avoid unnecessary recalculations
  // Use a more comprehensive hash that includes stop IDs and order to detect actual changes
  const routesKey = useMemo(() => {
    return routes.map((r) => {
      const stopIds = r.stops.map(s => s.stopId).join(',');
      const segmentCount = r.segments.length;
      const routeColor = r.color || generateRouteColor(r.id);
      const routeWidth = r.width ?? 6;
      return `${r.id}|${r.name}|${stopIds}|${segmentCount}|${r.isCirculator ? '1' : '0'}|${routeColor}|${routeWidth}`;
    }).join('||');
  }, [routes]);

  const stopsKey = useMemo(() => {
    return stops.map((s) => `${s.id}-${s.coordinates?.lat}-${s.coordinates?.lng}`).join('|');
  }, [stops]);
  
  // Track previous keys to prevent unnecessary syncs
  const prevRoutesKeyRef = useRef<string>('');
  const prevStopsKeyRef = useRef<string>('');
  // Track if we did a synchronous style-only update to prevent async function from running
  const didSynchronousUpdateRef = useRef<boolean>(false);

  // Sync routes to layer
  useEffect(() => {
    // A bumped resyncNonce forces a re-run even when the keys are unchanged — this is how the latest
    // state is rendered after a concurrent sync was dropped by the in-flight guard below.
    const isForcedResync = resyncNonce !== prevResyncNonceRef.current;
    prevResyncNonceRef.current = resyncNonce;
    // Skip if keys haven't actually changed (prevents unnecessary rerenders)
    if (!isForcedResync && routesKey === prevRoutesKeyRef.current && stopsKey === prevStopsKeyRef.current) {
      return;
    }

    // Reset synchronous update flag
    didSynchronousUpdateRef.current = false;

    // Update refs (must happen before the isConverting guard so deletes are not "lost")
    prevRoutesKeyRef.current = routesKey;
    prevStopsKeyRef.current = stopsKey;

    const validRouteIdsForPrune = new Set(routes.map((r) => r.id));
    removeFixedRouteLinesNotInRouteSet(global.mapLayers, validRouteIdsForPrune, editLayer);

    // Prevent starting a second full conversion while one is running; pruning above still applied.
    // The in-flight conversion owns clearing routeGenerationStatus when it finishes.
    if (isConvertingRef.current) {
      // Remember to run again when the in-flight conversion finishes, so the state we're seeing now
      // isn't lost (otherwise the latest stop's routed line can never appear). Coalesced: many drops
      // here collapse into a single trailing re-run.
      pendingResyncRef.current = true;
      return;
    }

    // CRITICAL FIRST STEP: Remove fixed route lines from ALL layers EXCEPT Fixed Routes layer
    // This must happen BEFORE any other processing to prevent routes from appearing in wrong layers
    const currentMapLayers = global.mapLayers;
    const validRouteIds = new Set(routes.map(r => r.id));
    
    // Find Fixed Routes layer first
    let fixedRoutesLayerId: number | null = null;
    const fixedRoutesLayer = currentMapLayers.find((layer) => layer.layerName === layerNameForCurrentSync);
    if (fixedRoutesLayer) {
      fixedRoutesLayerId = fixedRoutesLayer.layerId;
    }
    
    // Remove fixed route lines from all non-Fixed Routes layers immediately
    currentMapLayers.forEach((otherLayer) => {
      if (fixedRoutesLayerId !== null && otherLayer.layerId === fixedRoutesLayerId) {
        // Skip Fixed Routes layer
        return;
      }
      
      const hasFixedRouteLines = otherLayer.shapes.some((shape) => {
        if (shape.type === 'line') {
          const line = shape as Line;
          const metadata = (line as any).fixedRouteMetadata;
          return metadata && metadata.type === 'fixedRoute';
        }
        return false;
      });
      
      if (hasFixedRouteLines) {
        const cleanedShapes = otherLayer.shapes.filter((shape) => {
          if (shape.type === 'line') {
            const line = shape as Line;
            const metadata = (line as any).fixedRouteMetadata;
            if (metadata && metadata.type === 'fixedRoute') {
              return false; // Remove fixed route lines from other layers
            }
          }
          return true;
        });
        
        if (cleanedShapes.length !== otherLayer.shapes.length) {
          const cleanedLayer = {
            ...otherLayer,
            shapes: cleanedShapes,
          };
          editLayer(cleanedLayer, otherLayer.layerId);
        }
      }
    });
    
    // CRITICAL: Ensure Fixed Routes layer exists BEFORE processing routes
    // This prevents routes from being added to other layers
    let allFixedRoutesLayers = currentMapLayers.filter((layer) => layer.layerName === layerNameForCurrentSync);
    
    // If we have routes but no Fixed Routes layer, create it immediately and synchronously
    // This MUST happen before any route processing to prevent routes from being added to wrong layers
    if (routes.length > 0 && allFixedRoutesLayers.length === 0) {
      const newLayerId = idCounter;
      setIdCounter(idCounter + 1);
      const newLayer: Layer = {
        layerId: newLayerId,
        layerName: layerNameForCurrentSync,
        hidden: false,
        shapes: [],
        expanded: true,
      };
      // Synchronously update layers - don't use async setState
      global.setLayers((prevLayers) => {
        const otherLayers = prevLayers.filter((l) => l.layerName !== layerNameForCurrentSync);
        return [...otherLayers, newLayer];
      });
      // Update the layers array for subsequent code - use the newly created layer
      allFixedRoutesLayers = [newLayer];
      // Also update currentMapLayers reference to include the new layer
      // This ensures subsequent code sees the Fixed Routes layer
    }
    
    // If no routes exist, clean up all fixed route lines from the Fixed Routes layer
    if (routes.length === 0) {
      if (allFixedRoutesLayers.length > 0) {
        const currentFixedRoutesLayer = allFixedRoutesLayers[0];
        // Remove all fixed route lines, but keep other shapes (points, polygons, etc.)
        const shapesWithoutFixedRoutes = currentFixedRoutesLayer.shapes.filter((shape) => {
          if (shape.type === 'line') {
            const line = shape as Line;
            const metadata = (line as any).fixedRouteMetadata;
            // Remove all fixed route lines
            if (metadata && metadata.type === 'fixedRoute') {
              return false;
            }
          }
          // Keep non-fixed-route shapes
          return true;
        });
        
        // Only update if there were fixed route lines to remove
        if (currentFixedRoutesLayer.shapes.length !== shapesWithoutFixedRoutes.length) {
          const updatedLayer: Layer = {
            ...currentFixedRoutesLayer,
            shapes: shapesWithoutFixedRoutes,
          };
          editLayer(updatedLayer, currentFixedRoutesLayer.layerId);
        }
      }
      return;
    }

    // Get the latest Fixed Routes layers (may have been created above)
    // Always refresh from global.mapLayers to ensure we have the latest state after layer creation
    let latestFixedRoutesLayers = global.mapLayers.filter((layer) => layer.layerName === layerNameForCurrentSync);
    
    // If we just created a layer above but it's not in latestFixedRoutesLayers yet,
    // use the one we created (React state update might not be reflected yet)
    if (latestFixedRoutesLayers.length === 0 && allFixedRoutesLayers.length > 0) {
      // Use the layer we just created
      latestFixedRoutesLayers = allFixedRoutesLayers;
    }
    
    let layerId: number;
    let layer: Layer;

    if (latestFixedRoutesLayers.length > 0) {
      // Use the first Fixed Routes layer, remove any duplicates
      const primaryLayer = latestFixedRoutesLayers[0];
      layerId = primaryLayer.layerId;
      layer = primaryLayer;
      
      // If there are duplicates, remove them (keep only the primary layer)
      // Only filter Fixed Routes layers, leave all other layers untouched
      if (latestFixedRoutesLayers.length > 1) {
        global.setLayers((prevLayers) => {
          const fixedRoutesLayers = prevLayers.filter((l) => l.layerName === layerNameForCurrentSync);
          const otherLayers = prevLayers.filter((l) => l.layerName !== layerNameForCurrentSync);
          // Keep only the primary Fixed Routes layer, plus all other layers
          return [...otherLayers, primaryLayer];
        });
      }
    } else {
      // Create new layer
      layerId = idCounter;
      setIdCounter(idCounter + 1);
      layer = {
        layerId,
        layerName: layerNameForCurrentSync,
        hidden: false,
        shapes: [],
        expanded: true,
      };
      // Add layer to mapLayers, but first remove any existing Fixed Routes layers (defensive)
      // Preserve all other layers
      global.setLayers((prevLayers) => {
        const otherLayers = prevLayers.filter((l) => l.layerName !== layerNameForCurrentSync);
        return [...otherLayers, layer];
      });
    }

    // Pre-check: If all routes are style-only updates (no geometry recalculation needed),
    // update synchronously for instant feedback
    const currentLayer = global.mapLayers.find((l) => l.layerId === layerId) || layer;
    const existingRouteLinesMap = new Map<string, Line>();
    
    currentLayer.shapes.forEach((shape) => {
      if (shape.type === 'line') {
        const line = shape as Line;
        const metadata = (line as any).fixedRouteMetadata;
        if (metadata && metadata.type === 'fixedRoute' && metadata.fixedRouteId) {
          if (validRouteIds.has(metadata.fixedRouteId)) {
            existingRouteLinesMap.set(metadata.fixedRouteId, line);
          }
        }
      }
    });

    // Pre-check: If all routes are style-only updates (have existing lines with cached geometry),
    // update synchronously for instant feedback without async operations
    let allRoutesAreStyleOnly = true;
    const styleOnlyLines: Line[] = [];
    let hasAnyStyleChanges = false;
    
    for (const route of routes) {
      const routeStops = route.stops
        .map((stopConfig) => stops.find((s) => s.id === stopConfig.stopId))
        .filter((stop): stop is FixedRouteStop => stop !== undefined && stop.coordinates !== undefined);

      if (routeStops.length < 2) {
        // Skip routes with less than 2 stops - they won't be rendered anyway
        continue;
      }

      const currentStopsHash = computeRouteGeometryHash(route, routeStops);
      
      const existingLine = existingRouteLinesMap.get(route.id);
      const existingMetadata = existingLine ? (existingLine as any).fixedRouteMetadata : null;
      const wasCirculator = existingMetadata?.isCirculator || false;
      const isNowCirculator = route.isCirculator || false;
      const circulatorChanged = wasCirculator !== isNowCirculator;
      const togglingOnCirculator = !wasCirculator && isNowCirculator;
      const cachedStopsPart = route.geometry?.stopsHash ? getStopsHashPart(route.geometry.stopsHash) : '';
      const currentStopsPart = getStopsHashPart(currentStopsHash);
      const hasReturnLegGeometry =
        !!route.geometry?.returnSegmentGeometry &&
        route.geometry.returnSegmentGeometry.length > 0;
      const hasValidCachedGeometry = route.geometry?.coordinates &&
        route.geometry.coordinates.length > 0 &&
        route.geometry.stopsHash === currentStopsHash;
      const canReuseGeometryForCirculatorToggle =
        circulatorChanged &&
        cachedStopsPart !== '' &&
        cachedStopsPart === currentStopsPart &&
        // First toggle ON must still calculate return-leg geometry if we don't have it yet.
        (!togglingOnCirculator || hasReturnLegGeometry);
      
      if (existingLine && (hasValidCachedGeometry || canReuseGeometryForCirculatorToggle)) {
        // Style-only update - can be done synchronously
        // Generate color palette for routes without colors (same logic as in convertRoutesToLines)
        const routesWithoutColors = routes.filter(r => !r.color);
        const colorPalette = routesWithoutColors.length > 0 
          ? generateRouteColors(routesWithoutColors.length) 
          : [];
        const routeColorIndexMap = new Map<string, number>();
        routesWithoutColors.forEach((r, index) => {
          routeColorIndexMap.set(r.id, index);
        });
        const getRouteColor = (r: FixedRoute): string => {
          if (r.color) return r.color;
          const colorIndex = routeColorIndexMap.get(r.id);
          if (colorIndex !== undefined && colorPalette.length > 0) {
            return colorPalette[colorIndex % colorPalette.length];
          }
          return generateRouteColor(r.id);
        };
        const routeColor = getRouteColor(route);
        const routeWidth = route.width ?? 6;
        const colorChanged = existingLine.colour !== routeColor || existingLine.borderColour !== routeColor;
        const widthChanged = existingLine.borderWidth !== routeWidth;
        const nameChanged = existingLine.name !== route.name;
        const dashPattern = route.isCirculator ? '10, 5' : 'none';
        const dashChanged = existingLine.dashPattern !== dashPattern;
        
        if (colorChanged || widthChanged || nameChanged || dashChanged || circulatorChanged) {
          hasAnyStyleChanges = true;
          const cachedCoordsForToggle =
            circulatorChanged && route.geometry?.coordinates && route.geometry.coordinates.length > 1
              ? (route.geometry.coordinates as [number, number][])
              : undefined;
          // Preserve the exact same line ID to replace, not create duplicate
          const updatedLine: Line = {
            ...existingLine,
            id: existingLine.id, // Explicitly preserve ID to ensure replacement
            name: route.name,
            colour: routeColor,
            borderColour: routeColor,
            borderWidth: routeWidth,
            dashPattern,
            ...({
              fixedRouteMetadata: {
                ...(existingLine as any).fixedRouteMetadata,
                routeName: route.name,
                shortName: route.shortName,
                isCirculator: route.isCirculator,
              },
            } as any),
            ...(cachedCoordsForToggle ? { coordinates: cachedCoordsForToggle } : {}),
          };
          styleOnlyLines.push(updatedLine);
        } else {
          // No changes - keep existing line as-is
          styleOnlyLines.push(existingLine);
        }
      } else {
        // Needs geometry recalculation or doesn't have existing line - not a style-only update
        allRoutesAreStyleOnly = false;
        break; // Exit early - we need async conversion
      }
    }

    // If all routes are style-only updates, update synchronously and return immediately
    // This ensures instant color/width changes without any async delays or duplicates
    if (allRoutesAreStyleOnly && styleOnlyLines.length > 0) {
      // Remove all existing fixed route lines and replace with updated ones
      // This ensures we replace, not duplicate
      const otherShapes = currentLayer.shapes.filter((shape) => {
        if (shape.type === 'line') {
          const line = shape as Line;
          const metadata = (line as any).fixedRouteMetadata;
          if (metadata && metadata.type === 'fixedRoute') {
            return false; // Remove all fixed route lines, we'll add updated ones
          }
        }
        return true; // Keep non-fixed-route shapes (points, polygons, etc.)
      });

      const updatedLayer: Layer = {
        ...currentLayer,
        shapes: [...otherShapes, ...styleOnlyLines],
      };
      editLayer(updatedLayer, layerId);
      // Mark that we did a synchronous update to prevent async function from running
      didSynchronousUpdateRef.current = true;
      // No async (re)generation is happening on this synchronous style-only path, so
      // clear any "updating route" status that an edit handler may have optimistically
      // set. Otherwise the toast can stick forever (BUG #15a) because the async
      // convertRoutesToLines() that normally clears it never runs.
      setRouteGenerationStatus({ isGenerating: false, routeName: null });
      // Exit early - no async work needed, prevent duplicate route creation
      // The return here prevents convertRoutesToLines() from being called
      return;
    }

    // Convert routes to lines (only show actual geometry, never straight-line fallbacks)
    const convertRoutesToLines = async () => {
      // Double-check: if we already did a synchronous update, don't run async conversion
      if (didSynchronousUpdateRef.current) {
        return;
      }
      isConvertingRef.current = true;
      
      try {
        const liveRouteIdSet = () => new Set(routesRef.current.map((r) => r.id));

        // Get the latest layer state to ensure we see any direct updates
        const currentLayerForConversion = global.mapLayers.find((l) => l.layerId === layerId) || layer;
        
        // Separate existing fixed route lines from other shapes
        // We'll preserve existing route lines that haven't changed
        const existingRouteLines = new Map<string, Line>();
        const otherShapes: Shape[] = [];
        
        currentLayerForConversion.shapes.forEach((shape) => {
          if (shape.type === 'line') {
            const line = shape as Line;
            const metadata = (line as any).fixedRouteMetadata;
            if (metadata && metadata.type === 'fixedRoute' && metadata.fixedRouteId) {
              // Only keep if route still exists in context (use ref: async may finish after delete)
              if (liveRouteIdSet().has(metadata.fixedRouteId)) {
                existingRouteLines.set(metadata.fixedRouteId, line);
              }
            } else {
              // Keep non-fixed-route shapes
              otherShapes.push(shape);
            }
          } else {
            otherShapes.push(shape);
          }
        });

        // Render every FixedRoute as its own line. Direction siblings from GTFS
        // import share gtfsRouteId but have distinct stop orders/geometry and
        // must both appear on the map.
        const routesToRender: FixedRoute[] = [...routes];

        // Generate color palette for routes that don't have colors
        // This ensures even routes imported before the color assignment changes get distinct colors
        const routesWithoutColors = routes.filter(r => !r.color);
        const colorPalette = routesWithoutColors.length > 0 
          ? generateRouteColors(routesWithoutColors.length) 
          : [];
        // Create a map of route ID to color index for quick lookup
        const routeColorIndexMap = new Map<string, number>();
        routesWithoutColors.forEach((route, index) => {
          routeColorIndexMap.set(route.id, index);
        });

        // Create lines (only using cached geometry or actual calculated geometry - no fallbacks)
        const immediateLines: Line[] = [];
        const routeConversionPromises: Promise<Line | null>[] = [];
        const routeById = new Map<string, FixedRoute>();
        const routeStopsById = new Map<string, FixedRouteStop[]>();
        
        // Helper function to get route color
        const getRouteColor = (route: FixedRoute): string => {
          if (route.color) {
            console.log(`🎨 Route "${route.name}" using assigned color: ${route.color}`);
            return route.color;
          }
          const colorIndex = routeColorIndexMap.get(route.id);
          if (colorIndex !== undefined && colorPalette.length > 0) {
            const fallbackColor = colorPalette[colorIndex % colorPalette.length];
            console.log(`🎨 Route "${route.name}" using palette color: ${fallbackColor}`);
            return fallbackColor;
          }
          const generatedColor = generateRouteColor(route.id);
          console.log(`🎨 Route "${route.name}" using generated color: ${generatedColor}`);
          return generatedColor;
        };

        for (const route of routesToRender) {
          const existingLine = existingRouteLines.get(route.id);

          // Fast path for simulation-imported routes with seeded geometry:
          // avoid stop-hash/segment recalculation work unless user has manually edited the route.
          if (
            route.simulationImport?.source === 'simulation_results' &&
            !route.isManuallyEdited &&
            route.geometry?.coordinates &&
            route.geometry.coordinates.length > 1
          ) {
            const routeColor = getRouteColor(route);
            const routeWidth = route.width ?? 6;
            const importedLine: Line = {
              id: existingLine?.id || route.lineId || `fixed-route-${route.id}`,
              layerId,
              type: 'line',
              name: route.name,
              description: `Fixed Route: ${route.name}`,
              colour: routeColor,
              opacity: '1.0',
              borderColour: routeColor,
              borderWidth: routeWidth,
              isLocked: false,
              coordinates: route.geometry.coordinates,
              dashPattern: route.isCirculator ? '10, 5' : 'none',
              arrowStyle: 'start',
              hidden: existingLine ? existingLine.hidden : (route.hidden ?? false),
              ...({
                fixedRouteMetadata: {
                  type: 'fixedRoute',
                  fixedRouteId: route.id,
                  routeName: route.name,
                  shortName: route.shortName,
                  isCirculator: route.isCirculator,
                },
              } as any),
            };
            immediateLines.push(importedLine);
            continue;
          }

          // Get route stops
          const routeStops = route.stops
            .map((stopConfig: { stopId: string; waitTimeMinutes: number }) => stops.find((s) => s.id === stopConfig.stopId))
            .filter((stop: FixedRouteStop | undefined): stop is FixedRouteStop => stop !== undefined && stop.coordinates !== undefined);

          if (routeStops.length < 2) {
            continue;
          }

          routeById.set(route.id, route);
          routeStopsById.set(route.id, routeStops);

          // Check if route has valid cached geometry (instant rendering)
          const currentStopsHash = computeRouteGeometryHash(route, routeStops);

          // existingLine already resolved above for this route.

          // If stops hash doesn't match, route has changed - must recalculate geometry
          const hasValidCachedGeometry = route.geometry?.coordinates &&
            route.geometry.coordinates.length > 0 &&
            route.geometry.stopsHash === currentStopsHash;

          // For manually edited routes, only short-circuit when geometry is still valid.
          // If geometry was cleared or hash changed (e.g. waypoint move), allow recomputation.
          if (route.isManuallyEdited && existingLine && hasValidCachedGeometry) {
            immediateLines.push(existingLine);
            continue;
          }
          
          if (
            existingLine &&
            hasValidCachedGeometry
          ) {
            // Route exists and geometry hasn't changed - check if style properties changed
            const routeColor = getRouteColor(route);
            const routeWidth = route.width ?? 6;
            // Always check color - routes might have gotten colors assigned after line was created
            const colorChanged = existingLine.colour !== routeColor || existingLine.borderColour !== routeColor;
            const widthChanged = existingLine.borderWidth !== routeWidth;
            const nameChanged = existingLine.name !== route.name;
            
            // ALWAYS update color for fixed routes to ensure they use route colors
            // This fixes the issue where routes have colors but lines are still blue
            if (colorChanged || widthChanged || nameChanged || existingLine.colour === '#3388ff' || existingLine.borderColour === '#3388ff') {
              const updatedLine: Line = {
                ...existingLine,
                id: existingLine.id, // Preserve ID to ensure replacement, not duplication
                name: route.name,
                colour: routeColor,
                borderColour: routeColor,
                borderWidth: routeWidth,
                ...({ fixedRouteMetadata: { ...(existingLine as any).fixedRouteMetadata, routeName: route.name, shortName: route.shortName } } as any),
              };
              if (colorChanged || existingLine.colour === '#3388ff' || existingLine.borderColour === '#3388ff') {
                console.log(`🎨 Updating line color for route "${route.name}": ${existingLine.colour || existingLine.borderColour} -> ${routeColor}`);
              }
              immediateLines.push(updatedLine);
            } else {
              // No changes - keep existing line as-is (already updated by direct edit)
              immediateLines.push(existingLine);
            }
            continue;
          }
          
          // If stops changed (hash doesn't match), always recalculate - don't use cached geometry
          if (
            route.geometry?.coordinates &&
            route.geometry.coordinates.length > 0 &&
            route.geometry.stopsHash === currentStopsHash
          ) {
            // Validate cached coordinates before using them
            const cachedCoords = route.geometry.coordinates;
            const validCoords = cachedCoords.filter((coord: any) => {
              if (!Array.isArray(coord) || coord.length < 2) return false;
              const lat = coord[0];
              const lng = coord[1];
              return typeof lat === 'number' && typeof lng === 'number' && 
                     !isNaN(lat) && !isNaN(lng) && 
                     isFinite(lat) && isFinite(lng);
            });
            
            // Only use cached geometry if it has at least 2 valid coordinates
            if (validCoords.length >= 2) {
              // Use cached geometry - render immediately without async work
              // Preserve hidden state from existing line if it exists (key fix for visibility preservation)
              // Priority: existing line hidden state > route.hidden > false
              const existingHidden = existingLine ? existingLine.hidden : (route.hidden ?? false);
              const routeColor = getRouteColor(route);
              const cachedLine: Line = {
                // Preserve existing line ID when available to ensure replacement, not duplication.
                id: existingLine?.id || route.lineId || `fixed-route-${route.id}`,
                layerId,
                type: 'line',
                name: route.name,
                description: `Fixed Route: ${route.name}`,
                colour: routeColor,
                opacity: '1.0',
                borderColour: routeColor,
                borderWidth: route.width ?? 6,
                isLocked: false,
                coordinates: validCoords as [number, number][],
                dashPattern: route.isCirculator ? '10, 5' : 'none',
                arrowStyle: 'start',
                hidden: existingHidden,
                ...({ fixedRouteMetadata: { type: 'fixedRoute', fixedRouteId: route.id, routeName: route.name, shortName: route.shortName, isCirculator: route.isCirculator } } as any),
              };
              immediateLines.push(cachedLine);
              continue;
            }
          }

          // Check if we can do an incremental update (only recalculate affected segments)
          // This is much faster when only a few stops changed or only circulator status changed
          const existingLineForNewRoute = existingRouteLines.get(route.id);
          
          // Skip recalculation for manually edited routes only when geometry remains valid.
          // Waypoint drags clear geometry and/or change hash, so those must recalculate.
          if (route.isManuallyEdited) {
            // Use existing line only when cached geometry still matches current route state.
            if (existingLineForNewRoute && hasValidCachedGeometry) {
              immediateLines.push(existingLineForNewRoute);
              continue;
            }
            // If no existing line but we still have valid cached geometry, use it
            if (hasValidCachedGeometry && route.geometry?.coordinates && route.geometry.coordinates.length > 0) {
              const routeColor = getRouteColor(route);
              // In this branch `existingLineForNewRoute` is guaranteed falsy.
              // Keep the optional variable, but avoid TS narrowing weirdness by reading from a stable alias.
              const existingLineId: string | undefined = undefined;
              const cachedLine: Line = {
                // Preserve existing line ID when available to ensure replacement, not duplication.
                id: existingLineId || route.lineId || `fixed-route-${route.id}`,
                layerId,
                type: 'line',
                name: route.name,
                description: `Fixed Route: ${route.name}`,
                colour: routeColor,
                opacity: '1.0',
                borderColour: routeColor,
                borderWidth: route.width ?? 6,
                isLocked: false,
                coordinates: route.geometry.coordinates,
                dashPattern: route.isCirculator ? '10, 5' : 'none',
                arrowStyle: 'start',
                hidden: route.hidden || false,
                ...({ fixedRouteMetadata: { type: 'fixedRoute', fixedRouteId: route.id, routeName: route.name, shortName: route.shortName, isCirculator: route.isCirculator } } as any),
              };
              immediateLines.push(cachedLine);
              continue;
            }
            // For manually edited routes with invalid/missing geometry, continue into normal
            // conversion/recalculation flow instead of leaving a stale line on the map.
          }
          
          let promise: Promise<Line | null>;
          
          // Try incremental update if we have existing geometry
          const oldStopsHash = route.geometry?.stopsHash;
          const oldStopsPart = oldStopsHash ? getStopsHashPart(oldStopsHash) : '';
          const oldStopCount = oldStopsPart ? oldStopsPart.split('|').length : 0;
          const newStopCount = routeStops.length;
          const stopCountDiff = Math.abs(newStopCount - oldStopCount);
          const sameStopsIgnoringMode = oldStopsPart === getStopsHashPart(currentStopsHash);
          
          // Check if only circulator status changed (stops are the same)
          const existingMetadata = existingLineForNewRoute ? (existingLineForNewRoute as any).fixedRouteMetadata : null;
          const wasCirculator = existingMetadata?.isCirculator || false;
          const isNowCirculator = route.isCirculator || false;
          const circulatorChanged = wasCirculator !== isNowCirculator;
          
          // Check if this is a reorder (same stops, different order)
          // Extract stop IDs from old and new hash to compare
          const oldStopIds = oldStopsPart
            ? oldStopsPart.split('|').map((h: string) => h.split(':')[0])
            : [];
          const newStopsPart = getStopsHashPart(currentStopsHash);
          const newStopIds = newStopsPart
            ? newStopsPart.split('|').map((h: string) => h.split(':')[0])
            : [];
          const isReorder = stopCountDiff === 0 &&
            oldStopIds.length === newStopIds.length &&
            oldStopIds.length > 0 &&
            oldStopIds.every((id: string) => newStopIds.includes(id)) &&
            !oldStopIds.every((id: string, idx: number) => id === newStopIds[idx]); // Order changed

          // Same stops in the same order, but the stop hash changed — i.e. a stop's coordinate moved
          // (snap-to-road after placement, or dragging a stop). Only the 1-2 legs touching the moved
          // stop need re-routing, NOT the whole route. Without this, every snap fell through to a full
          // re-route of all legs (the main source of "calling route too many times" during capture).
          const sameStopIdsSameOrder = stopCountDiff === 0 &&
            oldStopIds.length === newStopIds.length &&
            oldStopIds.length > 0 &&
            oldStopIds.every((id: string, idx: number) => id === newStopIds[idx]);
          const isGeometryOnlyChange = sameStopIdsSameOrder && oldStopsPart !== newStopsPart;
          
          // If only 1 stop was added/removed OR only circulator status changed OR it's a reorder, try incremental update
          if (
            route.geometry?.coordinates &&
            route.geometry.coordinates.length > 0 &&
            oldStopsHash &&
            newStopCount >= 2 &&
            ((stopCountDiff === 1) || (stopCountDiff === 0 && circulatorChanged && sameStopsIgnoringMode) || isReorder || isGeometryOnlyChange)
          ) {
            // Determine which segments are affected
            const affectedSegmentIndices: number[] = [];

            if (stopCountDiff === 1 || isGeometryOnlyChange) {
              // Stop was added/removed (possibly in the middle).
              // Recalculate only segments that are new in the current sequence.
              const oldStopHashes = oldStopsPart ? oldStopsPart.split('|').filter(Boolean) : [];
              const oldSegmentHashes = new Set<string>();
              for (let j = 0; j < oldStopHashes.length - 1; j++) {
                oldSegmentHashes.add(`${oldStopHashes[j]}|${oldStopHashes[j + 1]}`);
              }

              for (let i = 0; i < routeStops.length - 1; i++) {
                const currentStopId = routeStops[i].id;
                const nextStopId = routeStops[i + 1].id;
                const currentStopHash = `${currentStopId}:${routeStops[i].coordinates?.lat || 0}:${routeStops[i].coordinates?.lng || 0}`;
                const nextStopHash = `${nextStopId}:${routeStops[i + 1].coordinates?.lat || 0}:${routeStops[i + 1].coordinates?.lng || 0}`;
                const currentSegmentHash = `${currentStopHash}|${nextStopHash}`;
                if (!oldSegmentHashes.has(currentSegmentHash)) {
                  affectedSegmentIndices.push(i);
                }
              }
            } else if (isReorder) {
              // Route was reordered - identify which segments changed
              // Compare old stop order with new stop order to find changed segments
              for (let i = 0; i < routeStops.length - 1; i++) {
                const currentStopId = routeStops[i].id;
                const nextStopId = routeStops[i + 1].id;
                const currentStopHash = `${currentStopId}:${routeStops[i].coordinates?.lat || 0}:${routeStops[i].coordinates?.lng || 0}`;
                const nextStopHash = `${nextStopId}:${routeStops[i + 1].coordinates?.lat || 0}:${routeStops[i + 1].coordinates?.lng || 0}`;
                const currentSegmentHash = `${currentStopHash}|${nextStopHash}`;
                
                // Check if this segment existed in the old route
                const oldStopHashes = oldStopsPart.split('|');
                let segmentExists = false;
                for (let j = 0; j < oldStopHashes.length - 1; j++) {
                  const oldSegmentHash = `${oldStopHashes[j]}|${oldStopHashes[j + 1]}`;
                  if (oldSegmentHash === currentSegmentHash) {
                    segmentExists = true;
                    break;
                  }
                }
                
                // If segment doesn't exist in old route, it needs recalculation
                if (!segmentExists) {
                  affectedSegmentIndices.push(i);
                }
              }
            }
            // If only circulator changed, no regular segments affected (just return segment)
            // The incremental updater will handle detecting and calculating the return segment
            
            // Try incremental update
            promise = updateRouteGeometryIncremental(route, stops, affectedSegmentIndices)
              .then(async (updatedRoute) => {
                if (updatedRoute) {
                  // Successfully updated incrementally - now convert to line
                  const line = await convertRouteToLine(updatedRoute, stops, layerId);
                  line.layerId = layerId;
                  if (existingLineForNewRoute) {
                    line.hidden = existingLineForNewRoute.hidden;
                  }
                  return line;
                } else {
                  // Incremental update failed - fall back to full recalculation
                  return convertRouteToLine(route, stops, layerId)
                    .then((line) => {
                      line.layerId = layerId;
                      if (existingLineForNewRoute) {
                        line.hidden = existingLineForNewRoute.hidden;
                      }
                      return line;
                    })
                    .catch((error) => {
                      console.error(`Failed to convert route ${route.id} to line (fallback):`, error);
                      // Keep existing line on transient conversion failures (e.g. stop just added but not yet in store).
                      return existingLineForNewRoute ?? null;
                    });
                }
              })
              .catch((error) => {
                console.error(`Failed incremental update for route ${route.id}, falling back to full:`, error);
                // Fall back to full recalculation
                return convertRouteToLine(route, stops)
                  .then((line) => {
                    line.layerId = layerId;
                    if (existingLineForNewRoute) {
                      line.hidden = existingLineForNewRoute.hidden;
                    }
                    return line;
                  })
                  .catch((error) => {
                    console.error(`Failed to convert route ${route.id} to line (after incremental error):`, error);
                    return existingLineForNewRoute ?? null;
                  });
              });
          } else {
            // Full recalculation (many stops changed or no existing geometry)
            promise = convertRouteToLine(route, stops)
              .then((line) => {
                line.layerId = layerId;
                if (existingLineForNewRoute) {
                  line.hidden = existingLineForNewRoute.hidden;
                }
                return line;
              })
              .catch((error) => {
                console.error(`Failed to convert route ${route.id} to line:`, error);
                // Keep existing line on transient conversion failures (e.g. stop just added but not yet in store).
                return existingLineForNewRoute ?? null;
              });
          }

          routeConversionPromises.push(promise);
        }

        const persistComputedGeometry = (routeId: string, convertedLine: Line) => {
          const routeForGeometry = routeById.get(routeId);
          const routeStopsForGeometry = routeStopsById.get(routeId);
          if (!routeForGeometry || !routeStopsForGeometry || routeStopsForGeometry.length < 2) return;

          const computedFromConverter = (convertedLine as any).fixedRouteComputedGeometry as RouteGeometry | undefined;
          if (!computedFromConverter || !computedFromConverter.coordinates || computedFromConverter.coordinates.length < 2) return;

          const expectedHash = computeRouteGeometryHash(routeForGeometry, routeStopsForGeometry);
          const nextGeometry: RouteGeometry = {
            ...computedFromConverter,
            coordinates: convertedLine.coordinates as [number, number][],
            stopsHash: expectedHash,
          };

          const currentGeometry = routeForGeometry.geometry;
          const shouldPersist =
            !currentGeometry ||
            currentGeometry.stopsHash !== nextGeometry.stopsHash ||
            !currentGeometry.segmentGeometries ||
            (currentGeometry.segmentGeometries.length !== (nextGeometry.segmentGeometries?.length ?? 0));

          if (shouldPersist) {
            // Merge ONLY the geometry onto the latest route — never write back the whole
            // routeForGeometry snapshot, which is captured at sync start and may be missing a stop
            // the user added while this conversion was running (that stop would be silently dropped).
            persistRouteGeometry(routeId, nextGeometry);
          }
        };

        // Show cached routes immediately
        // Combine existing route lines (that we're reusing) with new/updated ones
        const routeLineIds = new Set(immediateLines.map(line => {
          const metadata = (line as any).fixedRouteMetadata;
          return metadata?.fixedRouteId || line.id;
        }));
        
        // Add any existing route lines that we're reusing but haven't processed yet
        // This ensures all valid routes are included
        existingRouteLines.forEach((existingLine, routeId) => {
          if (!routeLineIds.has(routeId)) {
            immediateLines.push(existingLine);
          }
        });
        
        // Cross-layer cleanup is handled once at the start of the sync effect.
        // Avoid rescanning and editing every non-fixed-routes layer in this hot conversion path.

        // Get the absolute latest layer state right before updating
        // This ensures we see any direct updates that happened (like from RouteStyleDialog)
        const latestLayer = global.mapLayers.find((l) => l.layerId === layerId) || currentLayerForConversion;
        
        // Create a map of new lines by route ID for quick lookup
        const newLinesByRouteId = new Map<string, Line>();
        immediateLines.forEach((line) => {
          const metadata = (line as any).fixedRouteMetadata;
          const routeId = metadata?.fixedRouteId;
          if (routeId) {
            newLinesByRouteId.set(routeId, line);
          }
        });
        
        // Get the latest layer state again after cleaning other layers
        const currentLatestLayer = global.mapLayers.find((l) => l.layerId === layerId) || latestLayer;
        
        // Filter out any deleted route lines and preserve existing lines that already match
        const preservedShapes = currentLatestLayer.shapes.filter((shape) => {
          if (shape.type === 'line') {
            const line = shape as Line;
            const metadata = (line as any).fixedRouteMetadata;
            if (metadata && metadata.type === 'fixedRoute' && metadata.fixedRouteId) {
              const routeId = metadata.fixedRouteId;
              
              // Remove if route no longer exists
              if (!liveRouteIdSet().has(routeId)) {
                return false;
              }
              
              // Check if there's a new line for this route
              const newLine = newLinesByRouteId.get(routeId);
              if (newLine) {
                // Compare existing line with new line - if they match exactly, keep existing to prevent duplicate
                const styleMatches = 
                  line.colour === newLine.colour &&
                  line.borderColour === newLine.borderColour &&
                  line.borderWidth === newLine.borderWidth &&
                  line.name === newLine.name;
                
                // Compare coordinates: Line uses coordinates: [number, number][] format [lat, lng]
                const geometryMatches = 
                  line.coordinates && newLine.coordinates &&
                  line.coordinates.length === newLine.coordinates.length &&
                  line.coordinates.every((coord, idx) => {
                    const newCoord = newLine.coordinates![idx];
                    return coord[0] === newCoord[0] && coord[1] === newCoord[1];
                  });
                
                // If they match exactly, keep the existing line (don't add the new one)
                // Otherwise, remove the existing line (the new one will replace it)
                if (styleMatches && geometryMatches) {
                  // Remove this new line from the map so it won't be added
                  newLinesByRouteId.delete(routeId);
                  return true; // Keep existing line
                } else {
                  return false; // Remove existing line, new one will replace it
                }
              }
              
              // No new line for this route, but route still exists - keep existing line
              return true;
            }
          }
          // Keep non-fixed-route shapes
          return true;
        });
        
        // Add only the new lines that don't have matching existing lines
        const finalShapes = [...preservedShapes, ...Array.from(newLinesByRouteId.values())];
        
        const initialLayer: Layer = {
          ...currentLatestLayer,
          shapes: finalShapes,
        };
        editLayer(initialLayer, layerId);

        // If all routes were cached (and any deleted routes have been removed), we're done
        if (routeConversionPromises.length === 0) {
          // Clear route generation status if routes are done processing
          setRouteGenerationStatus({ isGenerating: false, routeName: null });
          return;
        }

        // Build final line set in one batched commit to avoid per-route layer churn.
        const completedLinesMap = new Map<string, Line>();
        immediateLines.forEach((line) => {
          const metadata = (line as any).fixedRouteMetadata;
          const routeId = metadata?.fixedRouteId || line.id;
          completedLinesMap.set(routeId, line);
        });

        const commitCompletedLines = () => {
          // Get current layer state to ensure we're working with the latest
          // Use global.mapLayers to get the most up-to-date state
          const currentLayer = global.mapLayers.find((l) => l.layerId === layerId) || layer;
          
          // Re-filter other shapes and ensure we only keep valid route lines
          const validRouteIdsSet = liveRouteIdSet();
          const validOtherShapes = currentLayer.shapes.filter((shape) => {
            if (shape.type === 'line') {
              const line = shape as Line;
              const metadata = (line as any).fixedRouteMetadata;
              if (metadata && metadata.type === 'fixedRoute' && metadata.fixedRouteId) {
                // Remove deleted routes
                return validRouteIdsSet.has(metadata.fixedRouteId);
              }
            }
            // Keep non-fixed-route shapes (points, polygons, etc.)
            return true;
          }).filter(shape => {
            // Filter out fixed route lines - we'll add them from completedLinesMap
            if (shape.type === 'line') {
              const line = shape as Line;
              const metadata = (line as any).fixedRouteMetadata;
              if (metadata && metadata.type === 'fixedRoute') {
                return false;
              }
            }
            return true;
          });

          // Combine: filtered other shapes + all route lines (immediate + newly converted)
          const allRouteLines = Array.from(completedLinesMap.values()).filter((line) => {
            const metadata = (line as any).fixedRouteMetadata;
            const rid = metadata?.fixedRouteId;
            return typeof rid === 'string' && validRouteIdsSet.has(rid);
          });

          const updatedLayer: Layer = {
            ...currentLayer,
            shapes: [...validOtherShapes, ...allRouteLines],
          };
          editLayer(updatedLayer, layerId);
        };

        // Wait for all conversions to complete, then do one final layer commit.
        const results = await Promise.allSettled(routeConversionPromises);
        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) {
            const metadata = (result.value as any).fixedRouteMetadata;
            const routeId = metadata?.fixedRouteId || result.value.id;
            if (metadata?.fixedRouteId) {
              persistComputedGeometry(metadata.fixedRouteId, result.value);
            }
            completedLinesMap.set(routeId, result.value);
          }
        });

        if (routeConversionPromises.length > 0) {
          commitCompletedLines();
        }

        // Clear route generation status now that all routes are rendered
        setRouteGenerationStatus({ isGenerating: false, routeName: null });
      } finally {
        isConvertingRef.current = false;
        if (pendingResyncRef.current) {
          // Sync requests arrived while this conversion ran — run once more so the latest
          // routes/stops are rendered (e.g. a stop's snapped coordinate that landed mid-conversion).
          pendingResyncRef.current = false;
          setResyncNonce((n) => n + 1);
        }
      }
    };

    convertRoutesToLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routesKey, stopsKey, layerNameForCurrentSync, resyncNonce]);
}

// Helper function to generate route color (moved from routeToLineConverter)
function generateRouteColor(routeId: string): string {
  let hash = 0;
  for (let i = 0; i < routeId.length; i++) {
    hash = routeId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 50%)`;
}

/**
 * Hook to render fixed routes on the map
 * This extends useRenderLines to include fixed routes
 */
export function useRenderFixedRoutes() {
  const context = useContext(AppContext);
  const { routes, stops } = useFixedRoutes();

  if (!context) {
    throw new Error('useRenderFixedRoutes must be used within AppContext');
  }

  const { global } = context;
  const { mapLayers } = global;

  // Find fixed routes layer
  const fixedRoutesLayer = mapLayers.find(
    (layer) => layer.layerName === FIXED_ROUTES_LAYER_NAME || layer.layerName === OPTIMISED_FIXED_ROUTES_LAYER_NAME
  );

  if (!fixedRoutesLayer || fixedRoutesLayer.hidden) {
    return [];
  }

  // Return lines from fixed routes layer
  // These lines are already rendered by useRenderLines, but we can add route-specific rendering here if needed
  return fixedRoutesLayer.shapes.filter((shape): shape is Line => shape.type === 'line');
}

/**
 * Hook to render stop markers for fixed routes
 * Returns markers with route color accents
 */
export function useRenderFixedRouteStopMarkers() {
  const context = useContext(AppContext);
  const { routes, stops, updateStop, editingRouteId } = useFixedRoutes();
  const [isCaptureMode, setIsCaptureMode] = useState(false);
  const stopNameRequestSeqRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const onStart = () => setIsCaptureMode(true);
    const onFinish = () => setIsCaptureMode(false);
    const onCancel = () => setIsCaptureMode(false);
    window.addEventListener('startFixedRouteCapture', onStart);
    window.addEventListener('finishFixedRouteCapture', onFinish);
    window.addEventListener('cancelFixedRouteCapture', onCancel);
    return () => {
      window.removeEventListener('startFixedRouteCapture', onStart);
      window.removeEventListener('finishFixedRouteCapture', onFinish);
      window.removeEventListener('cancelFixedRouteCapture', onCancel);
    };
  }, []);

  if (!context) {
    throw new Error('useRenderFixedRouteStopMarkers must be used within AppContext');
  }

  const { global } = context;
  const { mapLayers } = global;

  // Find fixed routes layer
  const fixedRoutesLayer = mapLayers.find(
    (layer) => layer.layerName === FIXED_ROUTES_LAYER_NAME || layer.layerName === OPTIMISED_FIXED_ROUTES_LAYER_NAME
  );

  return useMemo(() => {
    if (!fixedRoutesLayer || fixedRoutesLayer.hidden || routes.length === 0) {
      return [];
    }
    // Prefer the actual rendered line color so stop markers always match route lines.
    const lineColorByRouteId = new Map<string, string>();
    const hiddenRouteIds = new Set<string>();
    fixedRoutesLayer.shapes.forEach((shape) => {
      if (shape.type !== 'line') return;
      const line = shape as Line & { fixedRouteMetadata?: FixedRouteLineMetadata };
      const routeId = line.fixedRouteMetadata?.fixedRouteId;
      if (!routeId) return;
      if (line.hidden) {
        hiddenRouteIds.add(routeId);
      }
      const color = line.borderColour || line.colour;
      if (color) {
        lineColorByRouteId.set(routeId, color);
      }
    });

    // Early validation: filter out any stops with invalid coordinates before processing
    const validStops = stops.filter(stop => {
      if (!stop || !stop.coordinates) return false;
      const coords = stop.coordinates;
      const lat = coords?.lat;
      const lng = coords?.lng;
      return coords &&
        lat !== undefined &&
        lng !== undefined &&
        lat !== null &&
        lng !== null &&
        typeof lat === 'number' &&
        typeof lng === 'number' &&
        !isNaN(lat) &&
        !isNaN(lng) &&
        isFinite(lat) &&
        isFinite(lng);
    });

    const routesWithoutColors = routes.filter(r => !r.color);
    const colorPalette = routesWithoutColors.length > 0
      ? generateRouteColors(routesWithoutColors.length)
      : [];
    const routeColorIndexMap = new Map<string, number>();
    routesWithoutColors.forEach((r, index) => {
      routeColorIndexMap.set(r.id, index);
    });
    const getRouteColor = (r: FixedRoute): string => {
      const renderedColor = lineColorByRouteId.get(r.id);
      if (renderedColor) return renderedColor;
      if (r.color) return r.color;
      const colorIndex = routeColorIndexMap.get(r.id);
      if (colorIndex !== undefined && colorPalette.length > 0) {
        return colorPalette[colorIndex % colorPalette.length];
      }
      return generateRouteColor(r.id);
    };

    // Get all stops that are used in routes, grouped by route
    const stopMarkers: Array<{ stop: FixedRouteStop; routeColor: string; routeName: string; routeIds: string[] }> = [];
    routes.forEach((route) => {
      if (route.hidden || hiddenRouteIds.has(route.id)) return;
      const routeColor = getRouteColor(route);
      route.stops.forEach((stopConfig) => {
        const stop = validStops.find((s) => s.id === stopConfig.stopId);
        if (!stop) return;
        const existing = stopMarkers.find((m) => m.stop.id === stop.id);
        if (!existing) {
          stopMarkers.push({
            stop,
            routeColor,
            routeName: route.name,
            routeIds: [route.id],
          });
        } else {
          existing.routeIds.push(route.id);
          if (!existing.routeName.includes(route.name)) {
            existing.routeName = `${existing.routeName}, ${route.name}`;
          }
        }
      });
    });

    if (isCaptureMode) {
      const includedStopIds = new Set(stopMarkers.map((entry) => entry.stop.id));
      validStops.forEach((stop) => {
        if (includedStopIds.has(stop.id)) return;
        stopMarkers.push({
          stop,
          routeColor: '#78909c',
          routeName: 'Available stop',
          routeIds: [],
        });
      });
    }

    const validStopMarkers = stopMarkers.filter(({ stop }) => {
      // Double-check coordinates are valid before creating marker
      // Use strict validation matching the validation in the map function below
      if (!stop || !stop.coordinates) {
        return false;
      }
      
      const coords = stop.coordinates;
      const lat = coords.lat;
      const lng = coords.lng;
      
      // Strict validation - must be valid numbers, not undefined, null, NaN, or Infinity
      return coords && 
             lat !== undefined && 
             lng !== undefined &&
             lat !== null && 
             lng !== null &&
             typeof lat === 'number' && 
             typeof lng === 'number' &&
             !isNaN(lat) && 
             !isNaN(lng) &&
             isFinite(lat) &&
             isFinite(lng);
    });
  
    // Ensure unique keys by using index as fallback (though stop.id should be unique)
    return validStopMarkers
    .map(({ stop, routeColor, routeIds }, index) => {
      const stopServices = summarizeFixedRouteStopServices(
        stop.id,
        routes.filter((route) => routeIds.includes(route.id))
      );

      // Emphasize stops belonging to the route currently under edit (BUG #15b):
      // larger marker with a high-visibility ring so the edited route stands out
      // on the map among many routes.
      const isOnEditingRoute = editingRouteId != null && routeIds.includes(editingRouteId);
      const markerSize = isOnEditingRoute ? 22 : 16;
      const markerAnchor = markerSize / 2;
      const editRing = isOnEditingRoute
        ? 'box-shadow: 0 0 0 3px #29B6F6, 0 2px 4px rgba(0,0,0,0.3);'
        : 'box-shadow: 0 2px 4px rgba(0,0,0,0.3);';

      // Create custom marker icon with route color accent
      const stopIcon = new DivIcon({
        className: 'fixed-route-stop-marker',
        html: `<div style="
          background-color: ${routeColor};
          width: ${markerSize}px;
          height: ${markerSize}px;
          border-radius: 50%;
          border: 3px solid white;
          ${editRing}
          cursor: pointer;
        "></div>`,
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerAnchor, markerAnchor],
      });

      // CRITICAL: Final safety check before creating marker - ensure coordinates are valid
      // Extract coordinates safely to avoid accessing properties on undefined
      // This should never happen if validStops filter worked, but adding extra safety
      if (!stop || !stop.coordinates) {
        return null;
      }
      
      const coords = stop.coordinates;
      const lat = coords?.lat;
      const lng = coords?.lng;
      
      // Validate coordinates are valid numbers - this should never fail if validStops worked
      if (!coords || 
          lat === undefined || 
          lng === undefined ||
          lat === null || 
          lng === null ||
          typeof lat !== 'number' || 
          typeof lng !== 'number' ||
          isNaN(lat) || 
          isNaN(lng) ||
          !isFinite(lat) ||
          !isFinite(lng)) {
        return null;
      }

      // Double-check lat/lng are valid before creating marker
      // This should never throw if validation above passed, but adding try-catch for safety
      // Declare variables outside try block so they're accessible in catch block
      let finalLat: number | undefined;
      let finalLng: number | undefined;
      let numLat: number | undefined;
      let numLng: number | undefined;
      let position: [number, number] | undefined;
      
      try {
        // Final check: ensure lat and lng are actually numbers before passing to Marker
        // Re-extract to ensure values haven't changed
        finalLat = coords.lat;
        finalLng = coords.lng;
        
        if (typeof finalLat !== 'number' || typeof finalLng !== 'number' || 
            isNaN(finalLat) || isNaN(finalLng) || 
            !isFinite(finalLat) || !isFinite(finalLng) ||
            finalLat === undefined || finalLng === undefined ||
            finalLat === null || finalLng === null) {
          throw new Error(`Invalid coordinates: lat=${finalLat} (${typeof finalLat}), lng=${finalLng} (${typeof finalLng})`);
        }
        
        // Use array format [lat, lng] for position - more reliable than LatLng object
        // Ensure both values are explicitly numbers (not undefined/null) before creating array
        numLat = Number(finalLat);
        numLng = Number(finalLng);
        
        // Validate the converted numbers
        if (isNaN(numLat) || isNaN(numLng) || !isFinite(numLat) || !isFinite(numLng)) {
          throw new Error(`Converted coordinates invalid: lat=${numLat}, lng=${numLng} (from ${finalLat}, ${finalLng})`);
        }
        
        position = [numLat, numLng];
        
        // Final validation of the position array itself - ensure no undefined/null values
        if (position.length !== 2 || 
            position[0] === undefined || position[1] === undefined ||
            position[0] === null || position[1] === null ||
            typeof position[0] !== 'number' || typeof position[1] !== 'number') {
          throw new Error(`Position array invalid: [${position[0]} (${typeof position[0]}), ${position[1]} (${typeof position[1]})]`);
        }
        
        // Final check: Ensure position array is valid before passing to Marker
        if (position[0] === undefined || position[1] === undefined ||
            position[0] === null || position[1] === null ||
            typeof position[0] !== 'number' || typeof position[1] !== 'number' ||
            isNaN(position[0]) || isNaN(position[1]) ||
            !isFinite(position[0]) || !isFinite(position[1])) {
          return null;
        }
        
      const canDragStop = isCaptureMode || (editingRouteId != null && routeIds.includes(editingRouteId));

      return (
          <Marker
            key={`fixed-route-stop-${stop.id}-${index}`}
            position={position}
            icon={stopIcon}
            draggable={canDragStop}
            eventHandlers={{
              click: (e: any) => {
                if (!isCaptureMode) return;
                e?.originalEvent?.preventDefault?.();
                e?.originalEvent?.stopPropagation?.();
                const latlng = e?.target?.getLatLng?.();
                if (!latlng) return;
                window.dispatchEvent(new CustomEvent('fixedRouteCaptureLeftClick', {
                  detail: { lat: latlng.lat, lng: latlng.lng },
                }));
              },
              dragend: (e: any) => {
                if (!canDragStop) return;
                const latlng = e?.target?.getLatLng?.();
                if (!latlng) return;
                const movedCoords: [number, number] = [latlng.lat, latlng.lng];
                updateStop({
                  ...stop,
                  coordinates: { lat: movedCoords[0], lng: movedCoords[1] },
                });

                const nextSeq = (stopNameRequestSeqRef.current.get(stop.id) ?? 0) + 1;
                stopNameRequestSeqRef.current.set(stop.id, nextSeq);

                void (async () => {
                  try {
                    const nextName = await fixedRouteMarkerOsmService.getAddressForCoordinates(movedCoords);
                    const latestSeq = stopNameRequestSeqRef.current.get(stop.id);
                    if (latestSeq !== nextSeq) return;
                    if (!nextName || nextName.trim().length === 0) return;
                    updateStop({
                      ...stop,
                      name: nextName,
                      coordinates: { lat: movedCoords[0], lng: movedCoords[1] },
                    });
                  } catch {
                    // Keep existing stop name if reverse geocoding fails.
                  }
                })();
              },
            }}
          >
        <Popup>
          <div>
            <FixedRouteStopPopupContent
              stopName={stop.name || 'Unnamed Stop'}
              coordinates={[finalLat, finalLng]}
              address={stop.address}
              description={stop.description}
              services={stopServices}
            />
            {stop.simulationImport && (
              <div style={{ fontSize: '12px', color: '#444', marginTop: '8px', borderTop: '1px solid #ddd', paddingTop: '8px' }}>
                <div><strong>Stop Sequence:</strong> {stop.simulationImport.stopSequence}</div>
                <div><strong>Riders Picked Up:</strong> {stop.simulationImport.pickupCount}</div>
                <div><strong>Riders Dropped Off:</strong> {stop.simulationImport.dropoffCount}</div>
                {stop.simulationImport.pickedUpRiderIds.length > 0 && (
                  <div style={{ marginTop: '4px' }}>
                    <strong>Pickup Rider IDs:</strong> {stop.simulationImport.pickedUpRiderIds.join(', ')}
                  </div>
                )}
                {stop.simulationImport.droppedOffRiderIds.length > 0 && (
                  <div style={{ marginTop: '4px' }}>
                    <strong>Dropoff Rider IDs:</strong> {stop.simulationImport.droppedOffRiderIds.join(', ')}
                  </div>
                )}
              </div>
            )}
            {stop.mapPointId && (
              <div style={{ fontSize: '11px', color: '#999', marginTop: '8px', fontStyle: 'italic' }}>
                Linked to map point
              </div>
            )}
          </div>
        </Popup>
      </Marker>
    );
      } catch (error) {
        return null;
      }
    })
    .filter((marker) => marker !== null); // Remove any null markers from failed validation
  }, [fixedRoutesLayer, routes, stops, isCaptureMode, editingRouteId, updateStop]);
}

/**
 * Hook to render route waypoints on the map.
 *
 * Waypoints are rendered as white circles with colored outlines (inverse of stop markers).
 * By default we render only for the selected or editing route to avoid clutter.
 */
export function useRenderFixedRouteWaypointMarkers() {
  // Waypoint functionality is intentionally disabled.
  return [];
}

